/* eslint-disable react-hooks/exhaustive-deps */
import { Theme, themeToCssVars } from '@descope/page-editor-components';
import React, { useCallback, useLayoutEffect, useState } from 'react';
import FontFaceObserver from 'fontfaceobserver';
import { isEmpty, values } from 'lodash';
import { Box, Grid } from '@mui/material';
import checkerboard from '@components/checkerboard';
import { useFontsLoader } from 'hooks';
import Controls from './Controls';
import Preview from './Preview/Preview';
import { ThemeName, UpdateTheme } from '../types';
import { withUpdateInPath } from '../helpers';

type Props = {
	theme: Theme;
	updateTheme: UpdateTheme;
	themeName: ThemeName;
	// eslint-disable-next-line react/require-default-props
	sx?: React.ComponentProps<typeof Grid>['sx'];
};

export default ({ theme, updateTheme, themeName, sx = {} }: Props) => {
	const [styleCssVars, setStyleCssVars] = useState<Record<string, string>>({});

	const updateColor = useCallback(withUpdateInPath(updateTheme, 'colors'), [
		updateTheme
	]);
	const updateComponent = useCallback(
		withUpdateInPath(updateTheme, 'components'),
		[updateTheme]
	);
	const updateLogo = useCallback(withUpdateInPath(updateTheme, 'logo'), [
		updateTheme
	]);
	const updateTypography = useCallback(
		withUpdateInPath(updateTheme, 'typography'),
		[updateTheme]
	);

	useFontsLoader(theme?.typography?.fontFamilies);

	useLayoutEffect(() => {
		const { variants, fontFamilies } = theme?.typography || {};
		// in order to prevent flickering, we want to make sure all fonts are loaded before setting the css vars
		const fontsObservers = values(variants).flatMap((variant) => {
			const fontFamily = fontFamilies?.[Number(variant?.['font-family'])];
			if (!fontFamily?.url) return [];

			return [
				new FontFaceObserver(fontFamily.family[0], {
					weight: (variant as { 'font-weight'?: string })['font-weight']
				}).load()
			];
		});

		Promise.all(fontsObservers)
			.catch((e) => {
				// font is not loaded within the time range (3s), will use fallback font
				console.error('Font cannot be loaded', e); // eslint-disable-line
			})
			.finally(() => {
				setStyleCssVars(themeToCssVars(theme));
			});
	}, [theme]);

	return (
		<Grid
			data-testid="theme-settings"
			container
			spacing={2}
			data-theme={themeName}
			style={styleCssVars}
			sx={{
				...sx,
				backgroundColor: 'transparent',
				color: 'currentcolor'
			}}
		>
			<Grid item xs={8} height="100%">
				<Controls
					sx={{ px: 3 }}
					colors={theme?.colors}
					components={theme?.components}
					logo={theme?.logo}
					typography={theme?.typography}
					updateColor={updateColor}
					updateComponent={updateComponent}
					updateLogo={updateLogo}
					updateTypography={updateTypography}
				/>
			</Grid>
			<Grid item display="flex" xs={4} sx={{ background: checkerboard, px: 2 }}>
				<Box sx={{ margin: 'auto', width: '95%' }}>
					{/* We want to render the preview only after the css vars was loaded */}
					{isEmpty(styleCssVars) ? null : <Preview />}
				</Box>
			</Grid>
		</Grid>
	);
};
